/* @jsx h */
import { defineComponent, h, ref, watch, computed } from "vue";
import {
  type DateRange,
  type DateRangeOption,
  getDateRange,
} from "@lemmonhq/uikit";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { RevenueBars } from "../Overview/components/RevenueBars";
import type { RevenueSummary } from "../Overview/components/RevenueBars";
import { MonthlyPaymentBreakdown } from "./components/MonthlyPaymentBreakdown";
import { useRoute, useRouter } from "vue-router";
import { reportsAPI } from "@app/data/api/reports";
import type { RevenueDatum } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

function buildDateRangeOptions(t: (key: string) => string): DateRangeOption[] {
  return [
    { value: "this_week", label: t("reports.revenue.this_week") },
    { value: "this_month", label: t("reports.revenue.this_month") },
    { value: "this_quarter", label: t("reports.revenue.this_quarter") },
    { value: "this_year", label: t("reports.revenue.this_year") },
    { value: "last_week", label: t("reports.revenue.last_week") },
    { value: "last_month", label: t("reports.revenue.last_month") },
    { value: "last_quarter", label: t("reports.revenue.last_quarter") },
    { value: "last_year", label: t("reports.revenue.last_year") },
    { value: "last_12m", label: t("reports.revenue.last_12_months") },
    { value: "custom", label: t("reports.revenue.custom_range") },
  ];
}

const RANGE_KEYS = ["dateRange", "start", "end"];

export const RevenuePage = defineComponent({
  name: "RevenuePage",
  setup() {
    const auth = useAuth();
    const { t } = useLocalization();
    const route = useRoute();
    const router = useRouter();
    const dateRangeOptions = computed(() => buildDateRangeOptions(t));
    const resolution = ref<"day" | "week" | "month" | "quarter" | "year">(
      "day",
    );
    const revenueData = ref<RevenueDatum[]>([]);
    const loading = ref(false);
    const summary = ref<RevenueSummary>({
      totalRevenue: 0,
      averageRevenue: 0,
      categoriesCount: 0,
      groupBy: "day",
      selectedMethodLabel: "",
      selectedServiceMethodLabel: "",
    });

    const averagePeriodLabel = computed(() => {
      switch (summary.value.groupBy) {
        case "day":
          return t("reports.revenue.period_day");
        case "week":
          return t("reports.revenue.period_week");
        case "month":
          return t("reports.revenue.period_month");
        case "quarter":
          return t("reports.revenue.period_quarter");
        case "year":
          return t("reports.revenue.period_year");
        default:
          return t("reports.revenue.period_day");
      }
    });

    const rangeUnitsLabel = computed(() => {
      switch (summary.value.groupBy) {
        case "day":
          return t("reports.revenue.period_days");
        case "week":
          return t("reports.revenue.period_weeks");
        case "month":
          return t("reports.revenue.period_months");
        case "quarter":
          return t("reports.revenue.period_quarters");
        case "year":
          return t("reports.revenue.period_years");
        default:
          return t("reports.revenue.period_days");
      }
    });

    const resolveInitialRange = (): DateRange => {
      const query = route.query;
      const type =
        typeof query.dateRange === "string" ? query.dateRange : "this_month";

      if (
        type === "custom" &&
        typeof query.start === "string" &&
        typeof query.end === "string"
      ) {
        return {
          type: "custom",
          startDate: query.start,
          endDate: query.end,
        };
      }

      const calculated = getDateRange(type as any);
      return {
        type: type as any,
        startDate: calculated.startDate,
        endDate: calculated.endDate,
      };
    };

    const selectedRange = ref<DateRange>(resolveInitialRange());
    const printableRange = computed(() => {
      const range = selectedRange.value;
      if (range.type === "custom") {
        const start = new Date(range.startDate).toLocaleDateString();
        const end = new Date(range.endDate).toLocaleDateString();
        return `${start} – ${end}`;
      }
      const preset = dateRangeOptions.value.find(
        (option) => option.value === range.type,
      );
      const start = new Date(range.startDate).toLocaleDateString();
      const end = new Date(range.endDate).toLocaleDateString();
      return preset
        ? `${preset.label} (${start} – ${end})`
        : `${start} – ${end}`;
    });

    const extractRangeQuery = (
      query: Record<string, unknown>,
    ): Record<string, string> => {
      const result: Record<string, string> = {};
      if (typeof query.dateRange === "string") {
        result.dateRange = query.dateRange;
      }
      if (typeof query.start === "string") {
        result.start = query.start;
      }
      if (typeof query.end === "string") {
        result.end = query.end;
      }
      return result;
    };

    const buildRangeQuery = () => {
      const range = selectedRange.value;
      const query: Record<string, string> = {
        dateRange: range.type,
      };
      if (range.type === "custom") {
        query.start = range.startDate;
        query.end = range.endDate;
      }
      return query;
    };

    const isSameQuery = (next: Record<string, string>) => {
      const current = extractRangeQuery(route.query);
      if (Object.keys(current).length !== Object.keys(next).length) {
        return false;
      }
      return Object.keys(next).every((key) => current[key] === next[key]);
    };

    const updateQueryFromRange = () => {
      const next = buildRangeQuery();
      if (isSameQuery(next)) {
        return false;
      }

      const otherEntries = Object.entries(route.query).filter(
        ([key]) => !RANGE_KEYS.includes(key),
      );

      router.replace({
        query: {
          ...Object.fromEntries(otherEntries as [string, string][]),
          ...next,
        },
      });
      return true;
    };

    watch(
      () => route.query,
      () => {
        selectedRange.value = resolveInitialRange();
      },
    );

    updateQueryFromRange();

    const handleRangeChange = (range: DateRange | undefined) => {
      if (!range) return;
      selectedRange.value = range;
      updateQueryFromRange();
    };

    const handleSummaryChange = (next: RevenueSummary) => {
      summary.value = next;
    };

    const fetchRevenueData = async () => {
      loading.value = true;
      try {
        const data = await reportsAPI.getRevenueData({
          start_date_input: selectedRange.value.startDate,
          end_date_input: selectedRange.value.endDate,
        });
        revenueData.value = data;
      } catch (error) {
        console.error("Failed to fetch revenue data:", error);
        revenueData.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Watch for date range changes and fetch data
    watch(
      () => [selectedRange.value.startDate, selectedRange.value.endDate],
      () => {
        fetchRevenueData();
      },
      { immediate: true },
    );
    //
    return () => (
      <div class="w-full flex flex-col gap-6 overflow-hidden print:overflow-visible print:bg-white print:text-slate-900">
        <section class="min-h-0 flex-1 print:overflow-visible">
          <div class="flex h-full min-h-0 flex-col print:px-0 print:pt-0 print:pb-0">
            <RevenueBars
              dateRange={selectedRange.value}
              dateRangeOptions={dateRangeOptions.value}
              revenueData={revenueData.value}
              loading={loading.value}
              onRangeChange={handleRangeChange}
              onSummaryChange={handleSummaryChange}
              renderSummary={() => (
                <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  <div class="space-y-2">
                    <h2 class="text-3xl font-semibold text-accent flex items-center gap-3">
                      {t("reports.revenue.revenue_analytics")}
                    </h2>
                    <p class="text-sm text-base-content/70 max-w-3xl leading-relaxed">
                      <span class="hidden text-base-content/60 sm:inline">
                        {printableRange.value}
                      </span>
                    </p>
                  </div>

                  <div class="grid gap-4 sm:grid-cols-2 max-w-full xl:max-w-[700px] xl:flex-1">
                    <div class="rounded-2xl border  bg-base-100 p-3 shadow-lg transition hover:shadow-xl print:bg-white print:border-gray-300 print:shadow-none">
                      <div class="flex items-center justify-between mb-0">
                        <div class="flex items-center gap-3 flex-1">
                          <span class="inline-flex items-center justify-center rounded-xl border border-success/30 bg-success/10 p-2 text-success">
                            <span class="material-symbols-outlined text-lg">
                              trending_up
                            </span>
                          </span>
                          <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                            {t("reports.revenue.total_revenue")}
                          </span>
                        </div>
                        <div class="text-2xl font-bold text-accent mb-0 flex-1 text-right">
                          {formatPriceInLocale(
                            summary.value.totalRevenue,
                            true,
                            0,
                            auth.user.value?.restaurant.currency || "CHF",
                          )}
                        </div>
                      </div>
                    </div>

                    <div class="rounded-2xl border bg-base-100 p-3 shadow-lg transition hover:shadow-xl print:bg-white print:border-gray-300 print:shadow-none">
                      <div class="flex items-center gap-3 mb-0">
                        <span class="inline-flex items-center justify-center rounded-xl border border-info/30 bg-info/10 p-2 text-info">
                          <span class="material-symbols-outlined text-lg">
                            calculate
                          </span>
                        </span>
                        <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                          {t("reports.revenue.average")}
                        </span>
                        <div class="text-2xl font-bold text-accent mb-0 flex-1 text-right">
                          {formatPriceInLocale(
                            summary.value.averageRevenue,
                            true,
                            0,
                            auth.user.value?.restaurant.currency || "CHF",
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              onResolutionChange={(res) => {
                resolution.value = res;
              }}
              renderAfterChart={() => (
                <MonthlyPaymentBreakdown
                  dateRange={selectedRange.value}
                  resolution={resolution.value}
                  revenueData={revenueData.value}
                  loading={loading.value}
                />
              )}
            />
          </div>
        </section>
      </div>
    );
  },
});
